import React from "react";
import { Box } from "@mui/material";
import { pictureSources, SIZES } from "../../utils/responsiveImage";

/**
 * Props for {@link ResponsiveImage}.
 *
 * The intersection with `Record<string, any>` is deliberate: anything not
 * named here is forwarded to the underlying <img>, which is how call sites
 * pass handlers and data attributes. `checkJs` is on for this project, and
 * without an explicit shape `forwardRef` infers `{}` for props — which made
 * every prop on every call site a type error.
 *
 * @typedef {{
 *   src: string,
 *   alt?: string,
 *   sizes?: string,
 *   sx?: any,
 *   pictureSx?: any,
 *   loading?: "lazy" | "eager",
 * } & Record<string, any>} ResponsiveImageProps
 */

/**
 * An <img> that serves the density the display actually has.
 *
 * Drop-in for `<Box component="img" src=... sx=... />`: pass the original
 * asset path and the `sizes` for the box it sits in, and it renders a
 * <picture> offering AVIF then WebP then the original format, each with a
 * srcset. The `sx` lands on the <img>, so existing styling carries over
 * unchanged.
 *
 *   <ResponsiveImage src="/assets/images/family.png" sizes={SIZES.illustration} />
 *
 * `sizes` matters as much as the srcset does — without it the browser assumes
 * the image fills the viewport and fetches the 2x desktop file onto a phone.
 *
 * @type {React.ForwardRefExoticComponent<ResponsiveImageProps>}
 */
const ResponsiveImage = React.forwardRef(
  /**
   * @param {ResponsiveImageProps} props
   * @param {React.Ref<HTMLImageElement>} ref
   */
  function ResponsiveImage(
    { src, alt = "", sizes = SIZES.fullBleed, sx, pictureSx, loading = "lazy", ...rest },
    ref,
  ) {
    const resolved = pictureSources(src, sizes);

    // Reserve the right box before the bytes land, so adding srcset does not
    // reintroduce layout shift.
    //
    // This is `aspect-ratio` rather than the usual width/height attributes on
    // purpose: MUI's Box claims `width` and `height` as system props and
    // rewrites them into CSS, so the attributes never reach the DOM and the
    // numbers would silently become a hard pixel size instead.
    //
    // An array puts the ratio first and the caller's sx after, which is how
    // MUI orders precedence — and unlike an object spread it accepts an sx
    // that is itself an array or a callback.
    const mergedSx = resolved
      ? [{ aspectRatio: String(resolved.aspectRatio) }, ...(Array.isArray(sx) ? sx : [sx])]
      : sx;

    // Not in the manifest (a runtime blob URL, say) — behave like a plain img.
    if (!resolved) {
      return (
        <Box component="img" ref={ref} src={src} alt={alt} loading={loading} sx={sx} {...rest} />
      );
    }

    return (
      <Box component="picture" sx={{ display: "contents", ...pictureSx }}>
        {resolved.sources.map((source) => (
          <source key={source.type} type={source.type} srcSet={source.srcSet} sizes={source.sizes} />
        ))}
        <Box
          component="img"
          ref={ref}
          src={resolved.fallback}
          alt={alt}
          loading={loading}
          decoding="async"
          sx={mergedSx}
          {...rest}
        />
      </Box>
    );
  },
);

export default ResponsiveImage;
